import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="posthog"
export default class extends Controller {
  static values = { 
    userId: String,
    userProperties: Object
  }

  connect() {
    if(this.getUserProperties()['masquerading']) return
    this.handleAuthStateChange()
  }

  handleAuthStateChange() {
    const currentlyLoggedIn = this.isUserLoggedIn()
    const previouslyLoggedIn = this.getPreviousLoginState()
    const previousUserId = this.getPreviousUserId()

    // Login event detected
    if (currentlyLoggedIn && !previouslyLoggedIn) {
      this.handleLogin()
    }
    // Logout event detected  
    else if (!currentlyLoggedIn && previouslyLoggedIn) {
      this.handleLogout()
    }
    // User switched (different user logged in)
    else if (currentlyLoggedIn && previouslyLoggedIn && this.userIdValue !== previousUserId) {
      this.handleUserSwitch()
    }
    // Same user, check if we need to identify (first time in session)
    else if (currentlyLoggedIn && !this.hasIdentifiedInSession()) {
      this.identifyUser()
    }

    // Update stored state
    this.updateStoredState()
  }

  handleLogin() {
    console.log('PostHog: Login detected, identifying user')
    this.identifyUser()
    this.markIdentifiedInSession()
  }

  handleLogout() {
    console.log('PostHog: Logout detected, resetting PostHog')
    if (typeof posthog !== 'undefined') {
      posthog.reset(true) // Reset with device_id reset
    }
    this.clearSessionData()
  }

  handleUserSwitch() {
    console.log('PostHog: User switch detected, resetting and identifying new user')
    if (typeof posthog !== 'undefined') {
      posthog.reset(true)
    }
    this.clearSessionData()
    this.identifyUser()
    this.markIdentifiedInSession()
  }

  identifyUser() {
    if (typeof posthog !== 'undefined' && this.userIdValue) {
      const userProperties = this.getUserProperties()
      
      posthog.identify(this.userIdValue, userProperties)
      console.log('PostHog: User identified', { userId: this.userIdValue, properties: userProperties })
    }
  }

  getUserProperties() {
    // Return the user properties object, or empty object if not provided
    return this.hasUserPropertiesValue ? this.userPropertiesValue : {}
  }

  // State management helpers
  isUserLoggedIn() {
    return Boolean(this.userIdValue)
  }

  getPreviousLoginState() {
    return localStorage.getItem('posthog_was_logged_in') === 'true'
  }

  getPreviousUserId() {
    return localStorage.getItem('posthog_previous_user_id')
  }

  hasIdentifiedInSession() {
    const sessionKey = `posthog_identified_${this.userIdValue}`
    return sessionStorage.getItem(sessionKey) === 'true'
  }

  markIdentifiedInSession() {
    if (this.userIdValue) {
      const sessionKey = `posthog_identified_${this.userIdValue}`
      sessionStorage.setItem(sessionKey, 'true')
    }
  }

  updateStoredState() {
    const isLoggedIn = this.isUserLoggedIn()
    localStorage.setItem('posthog_was_logged_in', isLoggedIn.toString())
    localStorage.setItem('posthog_previous_user_id', this.userIdValue || '')
  }

  clearSessionData() {
    // Clear all posthog session data
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('posthog_identified_')) {
        sessionStorage.removeItem(key)
      }
    })
  }
} 
